import clsx from "clsx";
import moment from "moment";

import {
  Icon,
  KeyValueList,
  NoData,
  RenderWhen,
  Time
} from "../../../../../components";
import TagList from "../../../../samples/components/report_summary/cards/info/components/TagList";
import { DomainCertificate } from "../../../@types";

import styles from "./network-lists.module.scss";

type Props = {
  certificate: DomainCertificate;
};

const getIsValid = (validTo: string) => moment().isBefore(validTo);

const CertificateValidationResult = ({
  valid_to: validTo
}: Pick<DomainCertificate, "valid_to">) => {
  const isValid = getIsValid(validTo);
  const result = isValid ? "Valid" : "Expired";

  if (!validTo) return <NoData />;

  return (
    <div className="flex flex-gap-2">
      <Icon
        name={isValid ? "checkmark" : "close"}
        className={`threat-status--${isValid ? "goodware" : "malicious"}`}
      />
      {`${result} Certificate`}
    </div>
  );
};

const CertificateList = ({ certificate }: Props) => (
  <KeyValueList
    className="flex flex-column gap-default w-100"
    data-testid="nti_certs_list"
  >
    <KeyValueList.Item
      className="flex align-center flex-gap-1"
      label="Certificate Subject: "
      value={certificate.common_name}
    />
    <KeyValueList.Item
      className="flex align-center flex-gap-1"
      label="Issuer: "
      value={
        <RenderWhen
          condition={Boolean(certificate.issuer)}
          fallback={<NoData />}
        >
          {certificate.issuer}
        </RenderWhen>
      }
    />
    <KeyValueList.Item
      className="flex flex-gap-1 align-center"
      label="Validation Result: "
      value={<CertificateValidationResult valid_to={certificate.valid_to} />}
    />
    <KeyValueList.Item
      label="Valid From/To: "
      value={
        <>
          <Time time={certificate.valid_from} /> /{" "}
          <Time time={certificate.valid_to} />
        </>
      }
    />
    <KeyValueList.Item
      className="flex align-center flex-gap-1"
      label="Thumbprint: "
      value={
        <RenderWhen
          condition={Boolean(certificate.certificate_thumbprints.length)}
          fallback={<NoData />}
        >
          <TagList
            data={
              certificate.certificate_thumbprints.map(item => item.value) || []
            }
            renderTag={(item, className) => (
              <div
                className={clsx(
                  styles.certificate_item,
                  className,
                  "ui-inline"
                )}
                key={item}
              >
                {item}
              </div>
            )}
          />
        </RenderWhen>
      }
    />
    <KeyValueList.Item
      className="flex align-center flex-gap-1"
      label="Serial Number: "
      value={
        <RenderWhen
          condition={Boolean(certificate.serial_number)}
          fallback={<NoData />}
        >
          {certificate.serial_number}
        </RenderWhen>
      }
    />
    <KeyValueList.Item
      className="flex align-center flex-gap-1"
      keyClassName="white-space-nowrap"
      label={`Certificate Extensions (${
        certificate.extensions?.length || 0
      }): `}
      value={
        <RenderWhen
          condition={Boolean(certificate.extensions?.length)}
          fallback={<NoData />}
        >
          <TagList
            data={certificate.extensions.map(item => item.name)}
            renderTag={(item, className) => (
              <div
                key={item}
                className={clsx(
                  styles.certificate_item,
                  className,
                  "ui-inline"
                )}
              >
                {item}
              </div>
            )}
          />
        </RenderWhen>
      }
    />
  </KeyValueList>
);

export default CertificateList;
